import { FC } from 'react';
import { useIntl } from 'react-intl';
import { PaginationLink } from './PaginationLink';
import { msgs } from './messages';
import { DOTS, usePagination } from './usePagination';
import * as S from './styles';

type Props = {
  onPageChange: (page: number) => void;
  totalCount: number;
  siblingCount: number;
  currentPage: number;
  pageSize: number;
  generateHref: (pageNumber: number) => string;
};

export const Pagination: FC<Props> = ({
  onPageChange,
  totalCount,
  siblingCount,
  currentPage,
  pageSize,
  generateHref,
}) => {
  const intl = useIntl();
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });

  if (currentPage === 0 || !paginationRange || paginationRange.length < 2) {
    return null;
  }

  const onNext = () => {
    onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange(currentPage - 1);
  };

  const lastPage = paginationRange[paginationRange?.length - 1];
  return (
    <S.Wrapper data-testid="Pagination">
      <S.ArrowItem $disabled={currentPage === 1} onClick={onPrevious}>
        <PaginationLink
          data-testid="Previous-page"
          href={generateHref(currentPage - 1)}
          onClick={e => e.preventDefault()}
          disabled={currentPage === 1}
          aria-label={intl.formatMessage(msgs.prevPageLabel)}
        >
          <S.ArrowIcon name="chevronDown" $prev useCurrentColor />
        </PaginationLink>
      </S.ArrowItem>
      {paginationRange?.map((pageNumber, i) => {
        if (pageNumber === DOTS) {
          // eslint-disable-next-line react/no-array-index-key
          return <S.Dots key={pageNumber + '-' + i}>{DOTS}</S.Dots>;
        }

        return (
          <S.Item
            key={pageNumber}
            $selected={pageNumber === currentPage}
            onClick={() => onPageChange(pageNumber as number)}
          >
            <PaginationLink href={generateHref(+pageNumber)} onClick={e => e.preventDefault()}>
              {pageNumber}
            </PaginationLink>
          </S.Item>
        );
      })}
      <S.ArrowItem $disabled={currentPage === lastPage} onClick={onNext}>
        <PaginationLink
          data-testid="Next-page"
          href={generateHref(currentPage + 1)}
          onClick={e => e.preventDefault()}
          disabled={currentPage === lastPage}
          aria-label={intl.formatMessage(msgs.nextPageLabel)}
        >
          <S.ArrowIcon name="chevronDown" $next useCurrentColor />
        </PaginationLink>
      </S.ArrowItem>
    </S.Wrapper>
  );
};

export default Pagination;
